'use client'
import { useTranslation } from 'react-i18next'
import { type TextContentBlockProps } from '@/blocks/TextContentBlock'
import { ButtonNextLink } from '@/components/ButtonNextLink'
import * as GridLayout from '@/components/GridLayout/GridLayout'
import { ImageWithPlaceholder } from '@/components/ImageWithPlaceholder/ImageWithPlaceholder'
import { useProductData } from '@/components/ProductData/ProductDataProvider'
import { AddonComparisonDialogBody } from '@/features/addonComparison/AddonComparisonDialogBody'
import { AddonCoverageDialogBody } from '@/features/addonComparison/AddonCoverageDialogBody'
import { useProductAddonVariantsQuery } from '@/services/graphql/generated'
import { logger } from '@/services/logger/logger.client'
import type { ExpectedBlockType, SbBaseBlockProps, StoryblokAsset } from '@/services/storyblok/storyblok'
import { StoryblokComponent } from '@storyblok/react'
import { Button } from 'ui/src/components/Button/Button'
import { Card } from 'ui/src/components/Card/Card'
import { Dialog } from 'ui/src/components/Dialog/Dialog'
import { LargeDialog } from 'ui/src/components/Dialog/LargeDialog'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { card, imageContainer } from './AddonBlock.css'

// Home products show a table comparing travel coverage included in all home insurance vs the travel addon.
// All other products with addons show each addon's coverage info (perils).
const HOME_PRODUCT_NAMES = ['SE_HOUSE', 'SE_APARTMENT_RENT', 'SE_APARTMENT_BRF', 'SE_APARTMENT_STUDENT']

type AddonBlockProps = SbBaseBlockProps<{
  image: StoryblokAsset
  content: ExpectedBlockType<TextContentBlockProps>
}>

export function AddonBlock({ blok, nested }: AddonBlockProps) {
  const { t } = useTranslation()
  const productData = useProductData()

  const isHomeProduct = HOME_PRODUCT_NAMES.includes(productData.name)
  const baseTermsVersion = productData.variants.at(-1)?.termsVersion

  const { data: addonData } = useProductAddonVariantsQuery({
    variables: { productName: productData.name },
    skip: isHomeProduct,
    onError(error) {
      logger.error('Failed to fetch addon variants', {
        productName: productData.name,
        error: error.message,
        graphQLErrors: error.graphQLErrors?.map((e) => e.message),
      })
    },
  })
  const addonVariants = addonData?.product?.addonVariants ?? []
  const hasAddonVariants = addonVariants.length > 0

  const showComparisonDialog = isHomeProduct && Boolean(baseTermsVersion)
  const showAddonCoverageDialog = hasAddonVariants && !showComparisonDialog
  const showDialog = showComparisonDialog || showAddonCoverageDialog

  return (
    <GridLayout.Root nested={nested}>
      <GridLayout.Content width="1" align="center">
        <Card.Root className={card}>
          <div className={imageContainer}>
            <ImageWithPlaceholder src={blok.image.filename} alt={blok.image.alt} fill={true} />
          </div>

          <section className={yStack({ gap: 'sm', justifyContent: 'space-between' })}>
            {blok.content.map((nestedBlok) => (
              <StoryblokComponent key={nestedBlok._uid} blok={nestedBlok} nested={true} />
            ))}

            <footer className={xStack({ gap: 'xs' })}>
              <ButtonNextLink href={productData.priceCalculatorPageLink} variant="primary">
                {t('GET_PRICE_LINK')}
              </ButtonNextLink>
              {showDialog && (
                // 'order' here is used to display buttons according to design while giving keyboard navigation precedence
                //  to the button above - "See your price" button
                <LargeDialog.Root>
                  <Dialog.Trigger asChild>
                    <Button style={{ order: -1 }} variant="secondary">
                      {t('ADDON_BLOCK_WHATS_INCLUDED_BUTTON_LABEL')}
                    </Button>
                  </Dialog.Trigger>
                  <LargeDialog.Content>
                    <LargeDialog.CloseIconButton aria-label={t('DIALOG_CLOSE')} />
                    {showComparisonDialog ? (
                      <AddonComparisonDialogBody
                        baseTermsVersion={baseTermsVersion!}
                        addonTermsVersion="SE_TRAVEL_ADDON-HEDVIG-null-1"
                      />
                    ) : (
                      <AddonCoverageDialogBody addonVariants={addonVariants} />
                    )}
                  </LargeDialog.Content>
                </LargeDialog.Root>
              )}
            </footer>
          </section>
        </Card.Root>
      </GridLayout.Content>
    </GridLayout.Root>
  )
}
